import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { connect } from 'react-redux'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import config from 'constants/config'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ContactContext from 'contexts/contactContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'

interface Props {
  currentRegionCode: string
  supportAssistantEnabled: boolean
  supportAssistantFirst?: boolean
}

function HeaderContactOptionsModal(props: Props) {
  const {
    currentRegionCode,
    supportAssistantEnabled,
    supportAssistantFirst = false,
  } = props
  const intl = useIntl()
  const modalElement = useModalElementContext()
  const currentContact = useContext(ContactContext)
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  const localContact = currentContact.local
  const internationalContact = currentContact.international

  const copy = useMemo(() => {
    const isIndia = currentRegionCode.toUpperCase() === 'IN'

    if (isIndia) {
      return {
        title: intl.formatMessage(
          {
            id: 'fjRYFcEQdD',
            defaultMessage: 'We’re your travel companion',
            description: 'Contact options modal title for India region',
          },
        ),
        message: intl.formatMessage(
          {
            id: 'BuxOdtZL5H',
            defaultMessage: 'Our customer service team is available online or on the phone 7 days a week, 9am to 7pm.',
            description: 'Contact options modal message about availability for India region',
          },
        ),
      }
    }

    return {
      title: intl.formatMessage(
        {
          id: 'KdiRT0Yg+e',
          defaultMessage: 'We’re your 24/7 travel companion',
          description: 'Contact options modal title for default regions',
        },
      ),
      message: intl.formatMessage(
        {
          id: 'yy3UNJX9Vl',
          defaultMessage: 'Our customer service team is available online or on the phone all the time, whenever you need us.',
          description: 'Contact options modal message about availability for default regions',
        },
      ),
    }
  }, [intl, currentRegionCode])

  const handleItemClick = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  return (
    <Modal title={copy.title}>
      <VerticalSpacer gap={16}>
        <BodyText variant="large">{copy.message}</BodyText>
        <div>
          {supportAssistantFirst && supportAssistantEnabled && <ClickableListItem
            title={intl.formatMessage(
              {
                id: '//S2Rqu+8y',
                defaultMessage: 'Chat now',
                description: 'Chat now button title',
              },
            )}
            startIcon={<LineCommentsIcon />}
            subtitle={intl.formatMessage(
              {
                id: 'S+H7c5q9j3',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Support Assistant subtitle',
              },
            )}
            onClick={handleItemClick}
            to="/support"
            aria-label={intl.formatMessage(
              {
                id: 'HXIhayix6B',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Support Assistant aria label',
              },
            )}
            rel="nofollow"
            target="_blank"
          />}
          {shouldRenderTimely(currentRegionCode) &&
            <ClickableListItem
              title={intl.formatMessage(
                {
                  id: 'cZ9moq/JtG',
                  defaultMessage: 'Book store appointment',
                  description: 'Book store appointment button title',
                },
              )}
              startIcon={<img
                src={getSVGImageUrl('logo-LE')}
                width="24"
                height="24"
                alt={intl.formatMessage({
                  id: 'NdLD/3mTeC',
                  defaultMessage: 'LE store',
                  description: 'Alt text for Luxury Escapes store icon in contact options modal',
                })}
              />}
              onClick={handleItemClick}
              href={getTimelyLink()}
              aria-label={intl.formatMessage(
                {
                  id: 'pR/SReL24u',
                  defaultMessage: 'Timely booking',
                  description: 'Timely booking aria label',
                },
              )}
              rel="noopener"
              target="_blank"
            />}
          {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
            <ClickableListItem
              title={intl.formatMessage(
                {
                  id: '0e5yVQE6N6',
                  defaultMessage: 'Local Sales & Support',
                  description: 'Local Sales & Support button title',
                },
              )}
              startIcon={<LinePhoneIcon />}
              subtitle={localContact.humanReadable}
              onClick={handleItemClick}
              href={`tel:${localContact.number}`}
              aria-label={intl.formatMessage(
                {
                  id: 'ta41/XcJsU',
                  defaultMessage: 'Sales & Support contact',
                  description: 'Local Sales & Support aria label',
                },
              )}
              rel="nofollow"
            />
          )}
          {!supportAssistantFirst && supportAssistantEnabled && <ClickableListItem
            title={intl.formatMessage(
              {
                id: '//S2Rqu+8y',
                defaultMessage: 'Chat now',
                description: 'Chat now button title',
              },
            )}
            startIcon={<LineCommentsIcon />}
            subtitle={intl.formatMessage(
              {
                id: 'S+H7c5q9j3',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Support Assistant subtitle',
              },
            )}
            onClick={handleItemClick}
            to="/support"
            aria-label={intl.formatMessage(
              {
                id: 'HXIhayix6B',
                defaultMessage: 'Luxury Escapes Support Assistant',
                description: 'Support Assistant aria label',
              },
            )}
            rel="nofollow"
            target="_blank"
          />}
          {shouldRenderWhatsApp &&
            <ClickableListItem
              title={intl.formatMessage(
                {
                  id: 'bPSjzlypgH',
                  defaultMessage: 'WhatsApp',
                  description: 'WhatsApp button title',
                },
              )}
              startIcon={<img src={getSVGImageUrl('whatsapp-logo')} width="24" height="24" alt="WhatsApp" />}
              onClick={handleItemClick}
              href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
              aria-label={intl.formatMessage(
                {
                  id: '16S1qEJGuM',
                  defaultMessage: 'WhatsApp',
                  description: 'WhatsApp aria label',
                },
              )}
              rel="noopener"
              target="_blank"
            />}
          <ClickableListItem
            startIcon={<LinePhoneIcon />}
            title={intl.formatMessage(
              {
                id: 'pZ+DKQEp9f',
                defaultMessage: 'International Support Centre',
                description: 'International Support Centre button title',
              },
            )}
            subtitle={internationalContact.humanReadable}
            onClick={handleItemClick}
            href={`tel:${internationalContact.number}`}
            aria-label={intl.formatMessage(
              {
                id: 'WHWDZPg34Q',
                defaultMessage: 'International Support Centre contact',
                description: 'International Support Centre aria label',
              },
            )}
            rel="nofollow"
          />
        </div>
      </VerticalSpacer>
    </Modal>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentRegionCode: state.geo.currentRegionCode,
  supportAssistantEnabled: isSupportAssistantEnabled(state),
})

export default connect(mapStateToProps)(HeaderContactOptionsModal)
